import { create } from 'zustand';
import axios from '../lib/axios';

interface User {
  id: string;
  name: string;
  email: string;
  userFeedback: any;
  hasFeedback: boolean;
  wantsLawyerContact: boolean;
}

interface UserState {
  user: User | null;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string) => Promise<void>;
  registerAccount: (data: {
    email?: string;
    name?: string;
    cellphone?: string;
    companyName?: string;
    employeesNumber?: string;
    businessSegment?: string;
    userServiceId?: string;
    origin?: string | null;
  }) => Promise<void>;
  logout: () => void;
  validateToken: () => Promise<boolean>;
}

export const useUserStore = create<UserState>(set => ({
  user: null,
  isLoading: false,
  error: null,

  validateToken: async () => {
    const token = localStorage.getItem('token');
    console.log('Token:', token);
    if (!token) return false;

    try {
      const response = await axios.get('/users/profile');
      set({
        user: response.data.user || {
          id: response.data.userId,
          name: response.data.name,
          hasFeedback: response.data.hasFeedback,
          email: response.data.email,
        },
      });
      return true;
    } catch (error) {
      localStorage.removeItem('token');
      set({ user: null });
      return false;
    }
  },

  login: async (email: string, password: string) => {
    set({ isLoading: true, error: null });

    try {
      const response = await axios.post('/auth/login', {
        email,
        password,
      });
      console.log('Login response:', response.data);
      // Guardar token
      localStorage.setItem('token', response.data.access_token);

      // Salvar dados do usuário no store
      set({
        user: response.data.user || {
          id: response.data.userId,
          name: response.data.name,
          hasFeedback: response.data.hasFeedback,
          email,
        },
        isLoading: false,
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Erro ao fazer login',
        isLoading: false,
      });
      throw error; // Re-throw para que o componente de login possa tratar
    }
  },

  register: async (name: string, email: string, password: string) => {
    set({ isLoading: true, error: null });

    try {
      const response = await axios.post('/auth/register', {
        name,
        email,
        password,
      });

      // Guardar token
      localStorage.setItem('token', response.data.access_token);

      // Salvar dados do usuário no store
      set({
        user: response.data.user || {
          id: response.data.userId,
          name: response.data.name,
          hasFeedback: response.data.hasFeedback,
          email,
        },
        isLoading: false,
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Erro ao registrar',
        isLoading: false,
      });
      throw error; // Re-throw para que o componente de registro possa tratar
    }
  },

  registerAccount: async (data: {
    email?: string;
    name?: string;
    cellphone?: string;
    companyName?: string;
    employeesNumber?: string;
    businessSegment?: string;
    userServiceId?: string;
    origin?: string | null;
  }) => {
    set({ isLoading: true, error: null });

    try {
      const response = await axios.post('/auth/register/account', data);

      // Guardar token
      localStorage.setItem('token', response.data.access_token);

      // Salvar dados do usuário no store
      set({
        user: response.data.user || {
          id: response.data.userId,
          name: response.data.name,
          userFeedback: response.data.userFeedback,
          hasFeedback: response.data.hasFeedback,
          wantsLawyerContact: response.data.wantsLawyerContact,
          email: data.email,
        },
        isLoading: false,
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Erro ao registrar conta',
        isLoading: false,
      });
      throw error; // Re-throw para que o componente de registro possa tratar
    }
  },

  logout: () => {
    // Remover token
    localStorage.removeItem('token');
    set({ user: null });
  },
}));
